import { Link, useNavigate, useParams } from '@tanstack/react-router'
import { ArrowLeft } from 'lucide-react'
import { Button } from '@/components/ui'
import { QuoteDetail } from '../components/QuoteDetail'

/**
 * Full-screen quote detail page (Sprint 12c).
 *
 * Two routes share this component:
 *   - `/quotes/$quoteId` — view/edit an existing quote
 *   - `/quotes/new`      — create a new draft. After save it
 *                            navigates to `/quotes/{newId}`.
 *
 * Sprint 12d: New quote and Revise both navigate to this page so
 * the line editor gets the full viewport instead of the cramped
 * Drawer.
 */

export function QuoteDetailPage() {
  const params = useParams({ strict: false }) as { quoteId?: string }
  const navigate = useNavigate()
  const isCreate = !params.quoteId || params.quoteId === 'new'
  const id = !isCreate && params.quoteId ? Number(params.quoteId) : null
  const isValid =
    isCreate ||
    (id !== null && Number.isFinite(id) && id > 0)

  const titleSuffix = isCreate
    ? 'New'
    : id !== null
      ? `#${id}`
      : ''

  return (
    <div data-density="comfortable" className="flex h-full flex-col">
      <header className="flex items-center gap-3 border-b border-border-subtle bg-surface px-6 py-3">
        <Link to="/quotes">
          <Button size="sm" variant="ghost">
            <ArrowLeft size={14} />
            All quotes
          </Button>
        </Link>
        <span className="text-role-section-label">
          {isValid ? `Quote ${titleSuffix}` : 'Quote'}
        </span>
      </header>
      <div className="min-h-0 flex-1 overflow-auto">
        {!isValid ? (
          <div className="p-8 text-tertiary">Invalid quote id.</div>
        ) : isCreate ? (
          <QuoteDetail
            quoteId={null}
            onCreated={(newId) =>
              navigate({
                to: '/quotes/$quoteId',
                params: { quoteId: String(newId) },
                replace: true,
              })
            }
            onCancelCreate={() => navigate({ to: '/quotes' })}
          />
        ) : (
          <QuoteDetail quoteId={id} />
        )}
      </div>
    </div>
  )
}
